/**
 * MKS-503 — Ctrl+Enter (Cmd+Enter on macOS) inside a textarea submits the form
 * that owns it, anywhere in the SPA.
 *
 * It was reported for the time-entry description textarea, but every textarea
 * in Monksheets has the same problem: Enter inserts a newline (correctly), and
 * there was no keyboard way to save. Rather than wire a handler into each of
 * the ~13 components that pair a textarea with a form, this hook is mounted
 * ONCE at the top of `<App>` and listens on `document`, which reaches every
 * route and every modal, portalled ones included.
 *
 * The listener is on the BUBBLE phase and returns on `event.defaultPrevented`,
 * so any component that already handles Ctrl+Enter itself keeps winning — this
 * is a fallback, not an override.
 *
 * Scope: real `<form>` elements only. A modal built from a `<div>` and a button
 * has no form to submit and is left exactly as it is.
 *
 * Escape hatch: `data-no-ctrl-enter-submit` on the textarea or on any ancestor
 * turns the behaviour off for that form. Nothing uses it today — the ticket
 * asks for this app-wide — but it means an exclusion needs no change here.
 */
import { useEffect } from 'react';
import { useFlag } from '../services/flagsContext';

/** The attribute that opts a textarea (or a whole subtree) out of the shortcut. */
const OPT_OUT_ATTRIBUTE = 'data-no-ctrl-enter-submit';

/**
 * Submit-capable controls, in the order the browser itself would consider
 * them. A `<button>` with no `type` defaults to `submit`, so a bare button
 * counts.
 */
const SUBMIT_CONTROL_SELECTOR =
  'button[type="submit"], button:not([type]), input[type="submit"], input[type="image"]';

/**
 * The form's default submit button, or `null` when it has none.
 *
 * Document order decides, like the browser's own default-button lookup. The
 * `.form` check drops a control that sits inside this form in the DOM but is
 * associated with another one through its `form=` attribute — passing such a
 * control to `requestSubmit` would throw.
 */
function findSubmitter(form: HTMLFormElement): HTMLButtonElement | HTMLInputElement | null {
  const candidates = form.querySelectorAll<HTMLButtonElement | HTMLInputElement>(
    SUBMIT_CONTROL_SELECTOR
  );
  for (const candidate of candidates) {
    if (candidate.form === form) return candidate;
  }
  return null;
}

export function useCtrlEnterSubmit(): void {
  const enabled = useFlag('mks_503_ctrl_enter_textarea_submit');

  useEffect(() => {
    // Flag off is today's behaviour exactly: no listener at all.
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Someone closer to the textarea already dealt with this key.
      if (event.defaultPrevented) return;
      if (event.key !== 'Enter') return;
      // Ctrl on Windows/Linux, Cmd on macOS. Alt+Ctrl+Enter is a different
      // chord and not ours.
      if (event.altKey) return;
      if (!event.ctrlKey && !event.metaKey) return;

      const textarea = event.target;
      if (!(textarea instanceof HTMLTextAreaElement)) return;
      if (textarea.disabled || textarea.readOnly) return;
      if (textarea.closest(`[${OPT_OUT_ATTRIBUTE}]`)) return;

      // `.form` rather than `closest('form')`: it honours the `form=`
      // attribute, so a textarea rendered outside its own form still works.
      const form = textarea.form;
      if (!form) return;

      const submitter = findSubmitter(form);
      // A disabled submit button means the save is already in flight (or the
      // form is invalid). Do nothing rather than queue a second save.
      if (submitter?.disabled) return;

      // Firefox inserts a newline on Ctrl+Enter in a textarea, so the default
      // has to go even though the other browsers do nothing.
      event.preventDefault();
      form.requestSubmit(submitter ?? undefined);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled]);
}
